import { create } from 'zustand'
import type { ThreadIndexEntry } from './conversation-idb'

/**
 * Companion thread state: which thread is active, the index for the history
 * panel, and the hamburger slide-over's open flag. The conversation content
 * itself lives in the manager + conversation-idb; this store only points at
 * it.
 *
 * `activeThreadId` persists (one small key) so a reload resumes the thread —
 * `null` means "fresh chat": the thread is minted lazily on the first send.
 */

const THREAD_KEY = 'edge:companion:thread'

function readPersistedThreadId(): string | null {
  try {
    return localStorage.getItem(THREAD_KEY)
  } catch {
    return null
  }
}

function persistThreadId(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(THREAD_KEY)
    else localStorage.setItem(THREAD_KEY, id)
  } catch {
    /* ignore quota / unavailable storage */
  }
}

interface ThreadStoreState {
  /** The thread the conversation is writing into; null = fresh (lazy-created). */
  activeThreadId: string | null
  /** Thread index for the history panel, newest first. */
  threads: ThreadIndexEntry[]
  /** Whether the history slide-over/popover is open. */
  historyOpen: boolean

  /** Point the conversation at a thread (persisted). */
  setActiveThread: (id: string | null) => void
  /** "New chat": clear the pointer; the next send mints a thread. */
  startNewThread: () => void
  setThreads: (entries: ThreadIndexEntry[]) => void
  removeThread: (id: string) => void
  setHistoryOpen: (open: boolean) => void
}

export const useThreadStore = create<ThreadStoreState>((set) => ({
  activeThreadId: readPersistedThreadId(),
  threads: [],
  historyOpen: false,

  setActiveThread: (id) => {
    persistThreadId(id)
    set({ activeThreadId: id })
  },
  startNewThread: () => {
    persistThreadId(null)
    set({ activeThreadId: null })
  },
  setThreads: (threads) => set({ threads }),
  removeThread: (id) => set((state) => ({ threads: state.threads.filter((t) => t.id !== id) })),
  setHistoryOpen: (historyOpen) => set({ historyOpen }),
}))
